'use client';

import { createRoute, Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { type ReactNode, Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { WaitlistCTA } from '@/components/marketing/WaitlistCTA';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import {
  CLI_INSTALL_COMMAND_UNIX,
  CLI_INSTALL_COMMAND_WINDOWS,
  CLI_RELEASES_URL,
} from '@/lib/constants';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs',
  head: () => seoHead('/docs'),
  component: Docs,
});

const sections = [
  { id: 'install', label: 'Install' },
  { id: 'commands', label: 'Commands' },
  { id: 'review-categories', label: 'Categories' },
  { id: 'review-scope', label: 'Scope' },
  { id: 'suppressions', label: 'Suppressions' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'custom-rules', label: 'Custom Rules' },
  { id: 'gates', label: 'Gates' },
  { id: 'hooks', label: 'Hooks' },
  { id: 'ci', label: 'CI' },
  { id: 'configuration', label: 'Configuration' },
];

// Real MindQL rules, shown verbatim in the Custom rules section. Exported so the
// docs copy is asserted in CustomRuleExamples.test.tsx (the source strings stay
// plain text; the code/RefTable helpers are scoped inside DocsContent).
export const customRuleExamples = [
  {
    id: 'api_no_db',
    title: 'One rule, four languages',
    source: `rule api_no_db {
  match import i
  where i.file.path ~ "internal/api/" and i.imports_package("db*")
  block "api must not import a db package directly"
}`,
  },
  {
    id: 'long_http_handler',
    title: 'Flag oversized handlers',
    source: `rule long_http_handler {
  match func f
  where f.reachable_from("*Handler") and f.body_lines > 80
  emit minor "split this handler; it is doing too much"
}`,
  },
  {
    id: 'query_in_loop',
    title: 'Catch queries in loops',
    source: `rule query_in_loop {
  match call c
  where c.name ~ "Query" and c.in_loop
  emit minor "move this query out of the loop"
}`,
  },
];

// CI code snippets, shown verbatim in the Continuous integration section.
// Exported so the docs copy is asserted in CiExamples.test.tsx (the env var is
// always MIND_CI_TOKEN, never the internal override).
export const ciCodeExamples = {
  envVar: 'MIND_CI_TOKEN=mind_ci_xxxxx mind review --full-scan',
  loginToken: `mind login --token mind_ci_xxxxx
mind review`,
  githubActions: `- uses: actions/checkout@v4
  with:
    fetch-depth: 0            # so mind can diff against the base commit
- run: ${CLI_INSTALL_COMMAND_UNIX}
- run: mind review
  env:
    MIND_CI_TOKEN: \${{ secrets.MIND_CI_TOKEN }}`,
};

export const loginCodeExamples = {
  noBrowser: 'mind login --no-browser',
} as const;

// Exit codes mind review returns, shown in the Continuous integration section.
// Exported so CiExamples.test.tsx pins the 0-4 contract verbatim.
export const ciExitCodes = [
  { code: '0', meaning: 'Review passed.' },
  {
    code: '1',
    meaning:
      'A blocker or major finding fails the build. Also the fallback for an untyped error.',
  },
  {
    code: '2',
    meaning: 'Config or credential problem, for example a revoked token.',
  },
  { code: '3', meaning: 'Network or service problem.' },
  { code: '4', meaning: 'Invalid arguments.' },
];

type TableRow = { k: string; cells: ReactNode[] };

function RefTable({ headers, rows }: { headers: string[]; rows: TableRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="border-border border-b px-3 py-2 text-left font-semibold"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.k}>
              {row.cells.map((cell, j) => (
                <td
                  key={`${row.k}-${j}`}
                  className="border-border/40 text-foreground/80 border-b px-3 py-2 align-top"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocsContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  const cardStyle = {
    background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
    border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
  };
  const codeStyle = {
    background: theme.box.purpleNumber.bg[isDark ? 'dark' : 'light'],
    color: theme.box.purpleNumber.color[isDark ? 'dark' : 'light'],
  };
  const code = (text: string) => <code style={codeStyle}>{text}</code>;
  const h2 = 'mb-4 text-2xl font-bold md:text-3xl';
  const h3 = 'mb-3 text-lg font-semibold';
  const pre = 'overflow-x-auto rounded-md p-3 text-sm';

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[800px]">
            <Skeleton className="mx-auto mt-2 mb-8 h-12 w-[40%]" />
            <Skeleton className="mx-auto mb-2 h-6 w-[80%]" />
            <Skeleton className="mx-auto mb-8 h-6 w-[50%]" />
            <Skeleton className="mx-auto mb-12 h-14 w-full rounded-xl" />
            <div className="space-y-10">
              {['h-24', 'h-32', 'h-28', 'h-40', 'h-24', 'h-32'].map((h, i) => (
                <div key={i}>
                  <Skeleton className="mb-3 h-7 w-40" />
                  <Skeleton className={`${h} w-full rounded-xl`} />
                </div>
              ))}
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div
          className="mx-auto max-w-[800px]"
          style={{ fontFamily: theme.fonts.body }}
        >
          <h1
            className="text-gradient-hero mb-4 text-center text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Documentation
          </h1>
          <p
            className="text-foreground/70 mb-10 text-center text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            The full reference for the {code('mind')} CLI: install, commands,
            configuration, hooks, and suppressions. New here? Start with the{' '}
            <a
              href="/getting-started"
              className="underline"
              style={{ textDecorationColor: theme.colors.semantic.info }}
            >
              quick start
            </a>
            .
          </p>

          {/* Table of contents — sticky beneath the top nav on desktop */}
          <div className="bg-background mb-12 rounded-xl shadow-xl shadow-black/30 md:sticky md:top-20 md:z-40">
            <nav
              className="flex flex-wrap justify-center gap-x-3 gap-y-2 rounded-xl p-4 text-sm"
              style={cardStyle}
            >
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-foreground/70 hover:text-foreground transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="space-y-14">
            {/* Install */}
            <section id="install" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Install
              </h2>
              <p className="text-foreground/80 mb-2">
                Install the {code('mind')} CLI on macOS or Linux:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{CLI_INSTALL_COMMAND_UNIX}</code>
              </pre>
              <p className="text-foreground/80 mt-4 mb-2">
                On Windows, in PowerShell:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{CLI_INSTALL_COMMAND_WINDOWS}</code>
              </pre>
              <p className="text-foreground/80 mt-4">
                Re-run the same command any time to update. The installer places
                a single {code('mind')} binary in one location and touches
                nothing else: {code('/usr/local/bin')} or {code('~/.local/bin')}{' '}
                on macOS and Linux, {code('%LOCALAPPDATA%\\Programs\\mind')} on
                Windows.
              </p>

              <h3
                className="mt-6 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Install manually
              </h3>
              <p className="text-foreground/80 mb-2">
                Prefer to install by hand? Download the archive for your
                platform from the{' '}
                <a
                  href={CLI_RELEASES_URL}
                  className="underline"
                  style={{ textDecorationColor: theme.colors.semantic.info }}
                >
                  releases page
                </a>
                :
              </p>
              <ul className="text-foreground/80 list-disc space-y-2 pl-6">
                <li>
                  <span className="font-semibold">macOS / Linux:</span> download{' '}
                  {code('mind-<os>-<arch>.tar.gz')}, extract it with{' '}
                  {code('tar -xzf')}, make it executable with{' '}
                  {code('chmod +x mind')}, and move {code('mind')} onto a
                  directory in your {code('PATH')} (for example{' '}
                  {code('/usr/local/bin')}).
                </li>
                <li>
                  <span className="font-semibold">Windows:</span> download{' '}
                  {code('mind-windows-<arch>.zip')}, extract {code('mind.exe')},
                  and add its folder to your {code('PATH')}.
                </li>
              </ul>
              <h3
                className="mt-6 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Authenticate
              </h3>
              <p className="text-foreground/80 mb-2">
                However you installed it, sign in once before reviewing:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>mind login</code>
              </pre>
              <p className="text-foreground/80 mt-3 mb-2">
                Behind a proxy or signing in from a remote session? Keep the
                terminal command running and open its printed URL in your
                configured browser:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{loginCodeExamples.noBrowser}</code>
              </pre>
              <p className="text-foreground/80 mt-2">
                The browser must bypass the proxy for {code('localhost')},{' '}
                {code('127.0.0.1')}, and {code('::1')} so authentication can
                return to the waiting CLI.
              </p>

              <h3
                className="mt-6 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Review
              </h3>
              <p className="text-foreground/80 mb-2">
                From inside a repository connected to your GitHub App, review
                your changes (diff-only by default, or {code('--full-scan')} for
                the whole repo):
              </p>
              <pre className={pre} style={codeStyle}>
                <code>mind review</code>
              </pre>
            </section>

            {/* Commands */}
            <section id="commands" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Commands
              </h2>
              <RefTable
                headers={['Command', 'What it does']}
                rows={[
                  {
                    k: 'login',
                    cells: [
                      code('mind login'),
                      'Authenticate. Opens your browser.',
                    ],
                  },
                  {
                    k: 'login-no-browser',
                    cells: [
                      code(loginCodeExamples.noBrowser),
                      'Print the sign-in URL without opening a browser. Use it for proxies and remote sessions.',
                    ],
                  },
                  {
                    k: 'install',
                    cells: [
                      code('mind setup'),
                      'Detect your agent(s) and set up the stop hook auto-fix loop.',
                    ],
                  },
                  {
                    k: 'review',
                    cells: [
                      code('mind review'),
                      'Review your changed files. Diff-only by default.',
                    ],
                  },
                  {
                    k: 'config',
                    cells: [
                      code('mind config'),
                      'Show the resolved configuration and which layer supplied each value.',
                    ],
                  },
                  {
                    k: 'hook',
                    cells: [
                      code('mind hook'),
                      'Dispatch agent lifecycle hook events. The entry point your agent calls when it stops; you won’t run it by hand.',
                    ],
                  },
                  {
                    k: 'feedback',
                    cells: [
                      code('mind feedback'),
                      'Review past runs and record feedback on findings, or report a bug or feature request.',
                    ],
                  },
                  {
                    k: 'logout',
                    cells: [
                      code('mind logout'),
                      'Log out and remove stored credentials.',
                    ],
                  },
                  {
                    k: 'update',
                    cells: [
                      code('mind update'),
                      'Update the mind CLI to the latest release.',
                    ],
                  },
                  {
                    k: 'version',
                    cells: [code('mind version'), 'Print the CLI version.'],
                  },
                  {
                    k: 'help',
                    cells: [
                      code('mind --help'),
                      'List every command and flag.',
                    ],
                  },
                ]}
              />
            </section>

            {/* Review categories */}
            <section
              id="review-categories"
              className="scroll-mt-24 md:scroll-mt-44"
            >
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Review categories
              </h2>
              <p className="text-foreground/80 mb-4">
                Mindrealm reviews {code('Go')}, {code('Python')},{' '}
                {code('TypeScript')}, and {code('Rust')} across these
                categories:
              </p>
              <RefTable
                headers={['Category', 'Looks for']}
                rows={[
                  {
                    k: 'hygiene',
                    cells: [
                      'Hygiene',
                      'Dead code, unused exports, magic values, leftover markers, naming.',
                    ],
                  },
                  {
                    k: 'reliability',
                    cells: [
                      'Reliability',
                      'Unhandled errors, swallowed exceptions, resource leaks, missing timeouts.',
                    ],
                  },
                  {
                    k: 'architecture',
                    cells: [
                      'Architecture',
                      'Coupling, cohesion, duplication, oversized types and interfaces.',
                    ],
                  },
                  {
                    k: 'performance',
                    cells: [
                      'Performance',
                      'Work inside loops, unbounded growth, repeated expensive calls.',
                    ],
                  },
                  {
                    k: 'concurrency',
                    cells: [
                      'Concurrency',
                      'Unawaited calls, detached tasks, blocking work in async paths.',
                    ],
                  },
                  {
                    k: 'security',
                    cells: [
                      'Security',
                      'Hardcoded secrets, SQL/command/code injection, dangerous taint flow.',
                    ],
                  },
                  {
                    k: 'structure',
                    cells: [
                      'Structure',
                      'Long functions, deep nesting, long parameter lists, large files.',
                    ],
                  },
                  {
                    k: 'testing',
                    cells: [
                      'Testing',
                      'Missing or weak assertions, skipped tests, mock abuse, test pollution.',
                    ],
                  },
                  {
                    k: 'gates',
                    cells: [
                      'Language gates',
                      'Your toolchain linters and type checks (go vet, golangci-lint, ruff, tsc, eslint, cargo clippy).',
                    ],
                  },
                ]}
              />
              <p className="text-foreground/80 mt-4">
                Every review is deterministic: the same code returns the same
                findings every run. No model decides what gets flagged.
              </p>
            </section>

            {/* Review scope */}
            <section id="review-scope" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Review scope
              </h2>
              <p className="text-foreground/80 mb-3">
                {code('mind review')} reviews your changed files by default
                (diff-only), matching what the stop hook reviews. To scan the
                whole repository:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>mind review --full-scan</code>
              </pre>
              <p className="text-foreground/80 mt-4 mb-3">
                {code('--diff-only')} and {code('--full-scan')} are mutually
                exclusive, and {code('--commit')} implies diff-only. On a trial,
                reviews are diff-only by default; whole-repo{' '}
                {code('--full-scan')} is a paid-plan feature, though a trial
                organization can ask Mindrealm to enable it. Both are
                config-backed: set {code('diff_only')} or {code('full_scan')} in{' '}
                {code('mind.yaml')}.
              </p>
              <p className="text-foreground/80 mb-4">
                {code('mind review')} must run from inside a git repository
                whose {code('origin')} is a GitHub repo connected to the
                Mindrealm GitHub App. Outside a git repo, or with no GitHub
                origin, it fails fast with a clear message.{' '}
                <WaitlistCTA
                  label="Install the GitHub App"
                  source="docs_install_link"
                  variant="inline"
                />
                . After the GitHub App is installed for your organization,
                public repos the server can clone work as-is. A private repo not
                connected to that installation returns a clear not-connected
                message rather than a raw clone error.
              </p>
              <RefTable
                headers={['Flag', 'Effect']}
                rows={[
                  {
                    k: 'full-scan',
                    cells: [
                      code('--full-scan'),
                      'Scan the entire repository, not just changed files (paid plan).',
                    ],
                  },
                  {
                    k: 'diff-only',
                    cells: [
                      code('--diff-only'),
                      'Only report findings in changed files (the default).',
                    ],
                  },
                  {
                    k: 'commit',
                    cells: [
                      code('--commit <sha>'),
                      'Review files changed in a specific commit. Implies diff-only.',
                    ],
                  },
                  {
                    k: 'staged',
                    cells: [code('--staged'), 'Only staged changes.'],
                  },
                  {
                    k: 'unstaged',
                    cells: [code('--unstaged'), 'Only unstaged changes.'],
                  },
                  {
                    k: 'paths',
                    cells: [
                      code('mind review [paths...]'),
                      'Scope the review to specific files or directories.',
                    ],
                  },
                  {
                    k: 'rule-filter',
                    cells: [
                      code('--analyzers <id,...>'),
                      'Run only these rules, comma-separated.',
                    ],
                  },
                  {
                    k: 'profile',
                    cells: [
                      code('--profile <name>'),
                      <>
                        Run only the rules in a named profile. Overrides the{' '}
                        {code('mind.yaml')} profile; {code('--analyzers')} adds
                        to it.
                      </>,
                    ],
                  },
                  {
                    k: 'severity',
                    cells: [
                      code('--min-severity / --max-severity'),
                      'Bound the findings shown. One of info, minor, major, blocker.',
                    ],
                  },
                  {
                    k: 'show-suppressed',
                    cells: [
                      code('--show-suppressed'),
                      'Include suppressed findings in the output.',
                    ],
                  },
                  {
                    k: 'format',
                    cells: [code('--format text|json'), 'Output format.'],
                  },
                  {
                    k: 'enable-go-test',
                    cells: [
                      code('--enable-go-test'),
                      'Run the go-test gate (off by default for speed).',
                    ],
                  },
                  {
                    k: 'enable-pytest',
                    cells: [
                      code('--enable-pytest'),
                      'Run the pytest gate (off by default for speed).',
                    ],
                  },
                  {
                    k: 'enable-js-test',
                    cells: [
                      code('--enable-js-test'),
                      'Run the JS test gate, jest or vitest (off by default for speed).',
                    ],
                  },
                  {
                    k: 'enable-security-gates',
                    cells: [
                      code('--enable-security-gates'),
                      'Run the security gates, gitleaks secrets and osv-scanner dependency CVEs (off by default).',
                    ],
                  },
                  {
                    k: 'no-gates',
                    cells: [
                      code('--no-gates'),
                      'Disable all gates (linters, type checks).',
                    ],
                  },
                ]}
              />
              <p className="text-foreground/80 mt-4 mb-3">
                On a very large repository, a {code('--full-scan')} can exceed
                the review time budget (10 minutes by default). When it does,
                the review returns a <strong>partial result</strong>: the
                findings produced before the deadline, shown with a “Partial
                analysis” banner. It still blocks on any blocker or major
                finding, does not fail with zero findings, and the findings it
                reports are real. For a faster review on a huge repo, narrow the
                scope with {code('--diff-only')}, {code('--analyzers')}, or{' '}
                {code('--profile')}.
              </p>
            </section>

            {/* Suppressions */}
            <section id="suppressions" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Suppressions
              </h2>
              <p className="text-foreground/80 mb-3">
                Disagree with a finding? Silence one check with a{' '}
                {code('mind:disable')} comment on the line above the code. It
                stays recorded and reviewable, never hidden. Every suppression
                is meant to be temporary.
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`// mind:disable                 Go, TypeScript, Rust
# mind:disable                  Python
// mind:disable unused-export   silence one rule by id
// mind:disable unused-export - reason goes here`}</code>
              </pre>
              <p className="text-foreground/80 mt-4 mb-2">
                Suppress a range with a begin/end pair:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`// mind:disable-begin generated block
//   ... lines here are not reviewed ...
// mind:disable-end`}</code>
              </pre>
              <p className="text-foreground/80 mt-3">
                A bare {code('mind:disable')} silences every check on that line;
                naming an id silences just that one. Use Python&apos;s{' '}
                {code('#')} comment prefix, and {code('//')} for Go, TypeScript,
                and Rust.
              </p>
              <p className="text-foreground/80 mt-3">
                See suppressed findings with{' '}
                {code('mind review --show-suppressed')}, and require a reason on
                every directive with {code('require_reason')} in{' '}
                {code('mind.yaml')}.
              </p>
              <p className="text-foreground/80 mt-3">
                {code('mind:disable')} silences check findings in your code.
                Gate output (linters, type checks, tests) is turned off
                separately, with {code('disabled_analyzers')} or{' '}
                {code('--no-gates')}. See Gates.
              </p>
            </section>

            {/* Feedback */}
            <section id="feedback" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Feedback
              </h2>
              <p className="text-foreground/80 mb-3">
                {code('mind feedback')} opens an interactive menu to tell us
                when a finding was right or wrong, and to report bugs or request
                features. Run it with no arguments from anywhere you are signed
                in:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>mind feedback</code>
              </pre>

              <h3
                className="mt-6 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Give feedback on findings
              </h3>
              <p className="text-foreground/80 mb-3">
                Pick one of your recent reviews (run {code('mind review')} first
                if you have none yet), drill into a finding, and record your
                call. Each verdict takes an optional reason and is keyed to the
                finding&apos;s stable fingerprint, so it sticks even after you
                re-run the review.
              </p>
              <RefTable
                headers={['Your call', 'Means']}
                rows={[
                  {
                    k: 'agree',
                    cells: [code('agree'), 'You agree with the finding.'],
                  },
                  {
                    k: 'disagree',
                    cells: [code('disagree'), 'You disagree with the finding.'],
                  },
                  {
                    k: 'request-changes',
                    cells: [
                      code('request-changes'),
                      'You want changes before this finding is resolved.',
                    ],
                  },
                  {
                    k: 'manual-fix-applied',
                    cells: [
                      code('manual-fix-applied'),
                      'You fixed it by hand rather than as suggested.',
                    ],
                  },
                  {
                    k: 'skip',
                    cells: [
                      code('skip'),
                      'Move on without recording a verdict.',
                    ],
                  },
                ]}
              />

              <h3
                className="mt-6 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Report a bug or request a feature
              </h3>
              <p className="text-foreground/80">
                The same menu lets you file a bug report or a feature request:
                give a one-line summary, then a few lines of detail. Mindrealm
                tags it with your CLI version and current repository so we have
                the context to act on it.
              </p>
            </section>

            {/* Custom rules */}
            <section id="custom-rules" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Custom rules
              </h2>
              <p className="text-foreground/80 mb-4">
                MindQL lets you encode your team&apos;s standards as rules that
                run across Go, Python, TypeScript, and Rust: no build, no
                database, and deterministic findings on every run. Rules live in{' '}
                {code('.mind/rules/*.mql')} and run in every{' '}
                {code('mind review')}, right alongside the built-in checks.
              </p>

              <h3 className={h3} style={{ fontFamily: theme.fonts.display }}>
                One rule, four languages
              </h3>
              <p className="text-foreground/80 mb-2">
                This rule keeps your API layer from importing a database package
                directly. The same {code('.mql')} fires on Go, Python,
                TypeScript, and Rust, with no per-language rewrite:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{customRuleExamples[0].source}</code>
              </pre>
              <p className="text-foreground/70 mt-2 text-sm">
                MindQL reads every language through the same import graph, so
                one rule covers them all. No model decides what gets flagged;
                the same code produces the same findings on every run.
              </p>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                What a rule looks like
              </h3>
              <p className="text-foreground/80 mb-3">
                A rule names what to match, filters it with a condition, then
                emits a finding at a fixed severity:
              </p>
              <div className="mb-4">
                <RefTable
                  headers={['Part', 'What it does']}
                  rows={[
                    {
                      k: 'match',
                      cells: [
                        code('match'),
                        'Pick what to look at: an import, function, call, type, symbol, file, or the whole review.',
                      ],
                    },
                    {
                      k: 'where',
                      cells: [
                        code('where'),
                        'Filter the match with a condition over import paths, call reachability, structure, and names.',
                      ],
                    },
                    {
                      k: 'def',
                      cells: [
                        code('def'),
                        'Name a reusable condition once and share it across rules.',
                      ],
                    },
                    {
                      k: 'action',
                      cells: [
                        'action',
                        'When the match passes the filter, emit a finding at this severity (the verbs are below), followed by the message your team sees.',
                      ],
                    },
                  ]}
                />
              </div>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                match: what you can read
              </h3>
              <p className="text-foreground/80 mb-3">
                {code('match')} picks what to look at. Each kind exposes
                attributes you filter on in {code('where')}. Glob arguments (in
                quotes) match names or paths; every entity also carries{' '}
                {code('file')} and {code('line')}.
              </p>
              <div className="mb-2">
                <RefTable
                  headers={['Match', 'What you can read']}
                  rows={[
                    {
                      k: 'import',
                      cells: [
                        code('import'),
                        'module, module_path, last_segment, segments, imports_package("glob")',
                      ],
                    },
                    {
                      k: 'func',
                      cells: [
                        code('func'),
                        'name, exported, is_method, receiver, body_lines, callers, caller_count, callees, calls("glob"), reachable_from("glob"), is_entrypoint("kind")',
                      ],
                    },
                    {
                      k: 'call',
                      cells: [
                        code('call'),
                        'name, in_loop, inside("kind") — a call site',
                      ],
                    },
                    {
                      k: 'type',
                      cells: [
                        code('type'),
                        'name, exported, method_count, field_count, exported_field_count, method_lines, method_names, is_god_object',
                      ],
                    },
                    {
                      k: 'symbol',
                      cells: [
                        code('symbol'),
                        'name, symbol_kind, exported, receiver',
                      ],
                    },
                    {
                      k: 'file',
                      cells: [
                        code('file'),
                        'path, language, is_test, is_generated, is_third_party, is_config, class, imports, symbols',
                      ],
                    },
                    {
                      k: 'node',
                      cells: [
                        code('node'),
                        'type, text, name, in_loop, in_test, inside("kind"), has("rule") — raw AST, for shapes the typed kinds do not cover',
                      ],
                    },
                    {
                      k: 'review',
                      cells: [
                        code('review'),
                        'diff.files (path, status, changed_lines, is_test), changed("glob") — the whole change',
                      ],
                    },
                  ]}
                />
              </div>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                where: filtering the match
              </h3>
              <p className="text-foreground/80 mb-3">
                {code('where')} keeps only the matches that satisfy a condition,
                built from the matched entity&apos;s attributes with these
                operators and helpers:
              </p>
              <div className="mb-2">
                <RefTable
                  headers={['Operator / form', 'What it does']}
                  rows={[
                    {
                      k: 'regex',
                      cells: [
                        code('~'),
                        'Regex match against a string: path ~ "internal/api/".',
                      ],
                    },
                    {
                      k: 'bool',
                      cells: [
                        <>
                          {code('and')} {code('or')} {code('not')}
                        </>,
                        'Combine conditions.',
                      ],
                    },
                    {
                      k: 'cmp',
                      cells: [
                        <>
                          {code('==')} {code('!=')} {code('>')} {code('<')}
                        </>,
                        'Compare values: body_lines > 80.',
                      ],
                    },
                    {
                      k: 'dot',
                      cells: [
                        code('.'),
                        'Read a nested attribute: i.file.path.',
                      ],
                    },
                    {
                      k: 'any',
                      cells: [
                        code('any(list, cond)'),
                        'True if any item matches: any(diff.files, .path ~ "src/").',
                      ],
                    },
                  ]}
                />
              </div>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                def: reusable conditions
              </h3>
              <p className="text-foreground/80 mb-3">
                {code('def')} names a condition once so you can share it across
                rules. It takes the matched entity as a parameter and returns a
                boolean:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`def is_handler(f) = f.is_entrypoint("http")

rule big_handler {
  match func f
  where is_handler(f) and f.body_lines > 80
  emit minor "split this handler; it is doing too much"
}`}</code>
              </pre>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                action: emit a finding
              </h3>
              <p className="text-foreground/80 mb-3">
                The action fires when a match passes the filter: a rule emits at
                one fixed severity, so a finding&apos;s weight is part of the
                rule, not a guess:
              </p>
              <div className="mb-2">
                <RefTable
                  headers={['Keyword', 'Severity', 'Meaning']}
                  rows={[
                    {
                      k: 'emit-blocker',
                      cells: [
                        code('emit blocker'),
                        'Blocker tier. Stops the change at a gate.',
                      ],
                    },
                    {
                      k: 'emit-major',
                      cells: [
                        code('emit major'),
                        'Major tier. Must be fixed before merge.',
                      ],
                    },
                    {
                      k: 'emit-minor',
                      cells: [
                        code('emit minor'),
                        'Minor tier. Worth addressing.',
                      ],
                    },
                    {
                      k: 'emit-info',
                      cells: [code('emit info'), 'Info tier. A small remark.'],
                    },
                  ]}
                />
              </div>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                More examples
              </h3>
              <h4
                className="text-foreground mt-6 mb-2 font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                {customRuleExamples[1].title}
              </h4>
              <p className="text-foreground/80 mb-2">
                Flag an oversized HTTP handler: a function reached from a{' '}
                {code('*Handler')} whose body runs over 80 lines.
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{customRuleExamples[1].source}</code>
              </pre>
              <h4
                className="text-foreground mt-6 mb-2 font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                {customRuleExamples[2].title}
              </h4>
              <p className="text-foreground/80 mb-2">
                Catch a database query that runs inside a loop, where it should
                be lifted out.
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{customRuleExamples[2].source}</code>
              </pre>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                Add a rule
              </h3>
              <p className="text-foreground/80 mb-2">
                Drop a {code('.mql')} file in {code('.mind/rules/')} and run{' '}
                {code('mind review')}. Rules are checked in with your code, so
                the whole team gets them:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`// .mind/rules/detection_suite_reminder.mql
// A rule we run on our own repo: when we change a check,
// it reminds us to add a test for it in the same change.
rule detection_suite_reminder {
  match review
  where any(diff.files,
      (.path ~ "internal/software_code_" and not .is_test)
      or .path == "internal/analysis/rule_ids.go")
    and not any(diff.files, .path ~ "internal/detectionsuite")
  emit minor "detection changed; add a detectionsuite regression fixture"
}`}</code>
              </pre>
              <p className="text-foreground/70 mt-2 text-sm">
                Commit {code('.mind/')} to version control so the whole team
                gets your rules.
              </p>

              <h3
                className={`${h3} mt-8`}
                style={{ fontFamily: theme.fonts.display }}
              >
                What works today
              </h3>
              <div className="mb-4">
                <RefTable
                  headers={['Available now', 'Coming next']}
                  rows={[
                    {
                      k: 'r1',
                      cells: [
                        'Match imports, functions, calls, types, symbols, files, and the whole review.',
                        'Metavariable patterns for matching code shapes.',
                      ],
                    },
                    {
                      k: 'r2',
                      cells: [
                        'Filter with conditions over import paths, call reachability, structure, and file class.',
                        'Autofix that rewrites the flagged code for you.',
                      ],
                    },
                    {
                      k: 'r3',
                      cells: [
                        'Classify each file (code, test, config, docs, generated) and flag god-object types.',
                        'A package entity for module coupling, instability, and dependency cycles.',
                      ],
                    },
                    {
                      k: 'r4',
                      cells: [
                        'Reusable conditions, fixed severities, deterministic findings, four languages.',
                        'Rules that carry their own tests.',
                      ],
                    },
                  ]}
                />
              </div>

              <div className="rounded-xl p-6" style={cardStyle}>
                <p className="text-foreground/70">
                  Want your team&apos;s standards encoded as rules without doing
                  it yourself?{' '}
                  <Link
                    to="/consulting"
                    className="underline"
                    style={{ textDecorationColor: theme.colors.semantic.info }}
                  >
                    I&apos;ll build them with you
                  </Link>
                  .
                </p>
              </div>
            </section>

            {/* Gates */}
            <section id="gates" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Gates
              </h2>
              <p className="text-foreground/80 mb-3">
                Gates run your own toolchain on the machine doing the review:
                the linters, type checks, builds, and (optionally) tests you
                already use. They run alongside the deterministic checks and
                merge into one report, so a compile error, a lint violation, and
                a design finding all land together. A failing gate can block the
                review.
              </p>
              <p className="text-foreground/80 mb-4">
                Because they run where your environment lives, gates catch what
                your machine already knows: real build and test failures, not
                guesses. The trade-off is speed. Linters and type checks are
                quick and run by default; the test gates are the slow ones and
                stay off until you opt in.
              </p>
              <p className="text-foreground/80 mb-4">
                Why let Mindrealm run them instead of asking the agent to?
                Agents are unreliable at running a whole toolchain. Asked to run
                several linters and the test suite, an agent often runs one, a
                couple, or none, then reports success anyway. The stop hook runs
                every gate every time and blocks on any failure, so the checks
                you depend on actually happen on every change.
              </p>
              <RefTable
                headers={['Gate', 'Tools', 'Default']}
                rows={[
                  {
                    k: 'build-type',
                    cells: [
                      'Build and type checks',
                      'go build, go vet, tsc, cargo check',
                      code('on'),
                    ],
                  },
                  {
                    k: 'lint',
                    cells: [
                      'Linters',
                      'golangci-lint, ruff, vulture, eslint, cargo clippy',
                      code('on'),
                    ],
                  },
                  {
                    k: 'modules',
                    cells: [
                      'Module hygiene',
                      'go mod tidy, go mod verify',
                      code('on'),
                    ],
                  },
                  {
                    k: 'tests',
                    cells: [
                      'Test runners (slow)',
                      'go test, cargo test, pytest, jest/vitest',
                      code('off'),
                    ],
                  },
                ]}
              />
              <h3
                className={`${h3} mt-10`}
                style={{ fontFamily: theme.fonts.display }}
              >
                Test gates
              </h3>
              <p className="text-foreground/80 mt-4 mb-2">
                Turn the test gates on per project (or globally) when the extra
                seconds are worth it:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`# .mind/mind.yaml
gates:
  go_test: true
  cargo_test: true
  pytest: true
  js_test: true`}</code>
              </pre>
              <p className="text-foreground/80 mt-4">
                The JS test gate auto-detects jest or vitest from your{' '}
                {code('package.json')}. Or enable a test gate for a single run
                with {code('--enable-go-test')}, {code('--enable-pytest')}, or{' '}
                {code('--enable-js-test')}. To skip every gate for a faster,
                checks-only pass, use {code('mind review --no-gates')} or turn
                them off in config with {code('disabled_analyzers')} (see
                Configuration).
              </p>
              <p className="text-foreground/80 mt-4">
                The JS test gate detects jest and vitest today; node:test,
                mocha, and other runners are not supported yet. If a different
                test runner, another toolchain gate, or a project-specific
                custom gate would help your team,{' '}
                <Link
                  to="/contact"
                  className="underline"
                  style={{ textDecorationColor: theme.colors.semantic.info }}
                >
                  tell us what you run
                </Link>
                .
              </p>

              {/* Security gates (default-off) — subsection within Gates */}
              <h3
                className={`${h3} mt-10`}
                style={{ fontFamily: theme.fonts.display }}
              >
                Security gates
              </h3>
              <p className="text-foreground/80 mb-3">
                Two extra gates run dedicated security scanners over your repo:
                one for leaked secrets, one for vulnerable dependencies. They
                are <strong>off by default</strong>. Unlike the build, lint, and
                type gates, which key off the change in front of them, these
                scan the whole tree and are built for a deep, point-in-time
                security review, so they ship dark until you ask for them.
              </p>
              <RefTable
                headers={['Security gate', 'Tool', 'What it finds', 'Default']}
                rows={[
                  {
                    k: 'secrets',
                    cells: [
                      'Secrets',
                      'gitleaks',
                      'Hardcoded API keys, tokens, and credentials across any language (regex + entropy).',
                      code('off'),
                    ],
                  },
                  {
                    k: 'deps',
                    cells: [
                      'Dependency CVEs',
                      'osv-scanner',
                      'Known vulnerabilities in your dependencies. Reads Go, npm, PyPI, and Cargo lockfiles.',
                      code('off'),
                    ],
                  },
                ]}
              />
              <p className="text-foreground/80 mt-4 mb-2">
                Turn both on for a project (or globally) in config:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`# .mind/mind.yaml
gates:
  security: true`}</code>
              </pre>
              <p className="text-foreground/80 mt-4">
                Or enable them for a single run with{' '}
                {code('--enable-security-gates')}. The scanners are optional
                local tools: if {code('gitleaks')} or {code('osv-scanner')} is
                not installed on the machine running the review, that gate
                simply skips. It never fails the run. These gates are primarily
                meant for the in-depth security pass of a{' '}
                <Link
                  to="/consulting"
                  className="underline"
                  style={{ textDecorationColor: theme.colors.semantic.info }}
                >
                  consulting audit
                </Link>
                , not the per-change review loop.
              </p>
            </section>

            {/* Hook setup */}
            <section id="hooks" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Hook setup
              </h2>
              <p className="text-foreground/80 mb-4">
                {code('mind setup')} wires Mindrealm into your agent so it
                reviews every change automatically. It detects which agents are
                present (Claude Code, Codex, Kimi Code, and Antigravity CLI),
                asks which to set up, and asks where: globally in your home
                config (the default), or just the current project with{' '}
                {code('--local')}. Kimi Code is the exception: it reads hooks
                only from {code('~/.kimi-code/config.toml')}, so it is always
                set up globally. Each agent is covered below.
              </p>
              <p className="text-foreground/80 mb-4">
                Why a hook? Getting an agent to reliably review its own work or
                follow a rule does not work. It forgets, skips, or calls itself
                done. A stop hook runs every time the agent finishes, no matter
                what. When Mindrealm flags findings it blocks the stop and feeds
                them back as the agent's next instruction, so the agent fixes
                them before the work reaches you. That auto-fix loop works on
                Claude Code, Codex, Kimi Code, and Antigravity CLI.
              </p>
              <p className="text-foreground/80 mb-4">
                Skip the prompts with flags, for example{' '}
                {code(
                  'mind setup --platform claude-code,codex,kimi,antigravity --global --yes'
                )}
                .
              </p>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Claude Code
              </h3>
              <p className="text-foreground/80 mb-2">
                {code('mind setup --platform claude-code')} writes a Stop hook
                and the {code('Bash(mind:*)')} permission to{' '}
                {code('~/.claude/settings.json')} (or{' '}
                {code('.claude/settings.json')} with {code('--local')}). The
                hook runs as soon as it is written, with no approval step:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`{
  "hooks": {
    "Stop": [
      { "hooks": [{ "type": "command", "command": "mind hook" }] }
    ]
  },
  "permissions": { "allow": ["Bash(mind:*)"] }
}`}</code>
              </pre>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Codex
              </h3>
              <p className="text-foreground/80 mb-2">
                {code('mind setup --platform codex')} writes a Stop hook to{' '}
                {code('~/.codex/hooks.json')} (or {code('.codex/hooks.json')}{' '}
                with {code('--local')}):
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`{
  "hooks": {
    "Stop": [
      { "hooks": [{ "type": "command", "command": "mind hook" }] }
    ]
  }
}`}</code>
              </pre>
              <p className="text-foreground/80 mt-3">
                One extra step: Codex requires you to trust hooks before they
                run. After setup, start Codex and run {code('/hooks')} to review
                and trust the Mindrealm Stop hook. It stays inactive until
                trusted, and Codex re-prompts whenever the command changes.
              </p>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Kimi Code
              </h3>
              <p className="text-foreground/80 mb-2">
                {code('mind setup --platform kimi')} splices a managed hooks
                block into {code('~/.kimi-code/config.toml')} (
                {code('$KIMI_CODE_HOME/config.toml')} when that is set). Kimi
                Code reads hooks only from that file, so setup is always global
                and {code('--local')} does not apply. The block runs{' '}
                {code('mind hook')} when the agent stops, same as the other
                agents. Re-run setup any time to refresh the block; it replaces
                only what it manages.
              </p>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Antigravity CLI
              </h3>
              <p className="text-foreground/80 mb-2">
                {code('mind setup --platform antigravity')} writes a{' '}
                {code('mindrealm-review')} Stop hook to{' '}
                {code('~/.gemini/config/hooks.json')} (or{' '}
                {code('.agents/hooks.json')} with {code('--local')}). The Stop
                hook runs {code('mind hook')} when the agent finishes, same as
                the other agents.
              </p>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Removing the hook
              </h3>
              <p className="text-foreground/80 mb-2">
                Run {code('mind setup --uninstall')} to remove it (add{' '}
                {code('--platform claude-code')} to target one agent, or{' '}
                {code('--local')} for the current project only). It removes only
                what setup added, leaving your other hooks and permissions
                intact, and is a no-op when nothing is installed. On Codex you
                can instead disable the hook from inside the agent with{' '}
                {code('/hooks')}, without editing any files.
              </p>
              <p className="text-foreground/70 mt-3 text-sm">
                To wire it by hand, drop the matching block into the file above.
                The reviewer itself is agent-agnostic: {code('mind review')} and
                the GitHub App review code from any agent or author.
              </p>
            </section>

            {/* Continuous integration */}
            <section id="ci" className="scroll-mt-24 md:scroll-mt-44">
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Continuous integration
              </h2>
              <p className="text-foreground/80 mb-3">
                {code('mind login')} is interactive, so for CI generate a CI
                token from your dashboard and give it to the CLI headless. The
                token is organization-scoped, review-only, and revocable, and it
                stays valid for long runs, so a {code('--full-scan')} never
                expires mid-review.
              </p>
              <p className="text-foreground/80 mb-2">
                Provide it as the {code('MIND_CI_TOKEN')} environment variable:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{ciCodeExamples.envVar}</code>
              </pre>
              <p className="text-foreground/80 mt-4 mb-2">
                Or store it once with {code('mind login --token')} for repeated
                local headless use:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{ciCodeExamples.loginToken}</code>
              </pre>
              <p className="text-foreground/80 mt-4">
                The repository still needs the Mindrealm GitHub App installed:
                the token authenticates the caller, the app authorizes the repo.
              </p>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                GitHub Actions
              </h3>
              <p className="text-foreground/80 mb-2">
                Add the token as a repository secret, then run{' '}
                {code('mind review')} as a step. {code('fetch-depth: 0')} gives{' '}
                {code('mind')} the history it needs to compute the diff:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{ciCodeExamples.githubActions}</code>
              </pre>
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Exit codes
              </h3>
              <p className="text-foreground/80 mb-3">
                {code('mind review')} sets its exit code so a CI step fails the
                build on a blocking finding:
              </p>
              <RefTable
                headers={['Code', 'Meaning']}
                rows={ciExitCodes.map((e) => ({
                  k: e.code,
                  cells: [code(e.code), e.meaning],
                }))}
              />
              <h3
                className="mt-8 mb-3 text-lg font-semibold"
                style={{ fontFamily: theme.fonts.display }}
              >
                Machine-readable output
              </h3>
              <p className="text-foreground/80 mb-2">
                For machine-readable output use{' '}
                {code('mind review --format json')} (the default is{' '}
                {code('text')}); each finding carries {code('severity')},{' '}
                {code('filePath')}, {code('startLine')}, {code('description')},{' '}
                {code('remediation')}, and {code('rule_id')}.
              </p>
              <p className="text-foreground/80 mt-4">
                If your pipeline already runs its own linters, type checks, and
                tests, {code('mind review --no-gates')} skips Mindrealm's gate
                checks while the rest of the review still runs and still blocks
                on a must-fix.
              </p>
            </section>

            {/* Configuration */}
            <section
              id="configuration"
              className="scroll-mt-24 md:scroll-mt-44"
            >
              <h2 className={h2} style={{ fontFamily: theme.fonts.display }}>
                Configuration
              </h2>
              <p className="text-foreground/80 mb-3">
                {code('mind')} reads YAML config from two locations: a project
                file at {code('.mind/mind.yaml')} and a global file at{' '}
                {code('~/.config/mind/mind.yaml')}. The project file overrides
                the global one.
              </p>
              <p className="text-foreground/80 mb-3">
                Precedence, highest first: a command-line flag, then environment
                variables ({code('MIND_*')}), then {code('.env')} in the current
                directory, then the project {code('.mind/mind.yaml')}, then the
                global {code('~/.config/mind/mind.yaml')}, then built-in
                defaults.
              </p>
              <p className="text-foreground/80 mb-3">
                Only the highest layer that sets a key counts, so an edit to a
                lower one has no effect. {code('mind config')} prints every
                resolved key with the layer that supplied it, which is the
                fastest way to find out why an edit did nothing.
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`$ mind config
user config file     /home/you/.config/mind/mind.yaml  (found)
project config file  /your/repo/.mind/mind.yaml        (found)
.env                 /your/repo/.env                   (not found)

KEY               VALUE                 SOURCE
grpc_url          localhost:50051       environment MIND_GRPC_URL
task_grpc_url     localhost:50051       project config file
auth_token        (set, not shown)      credentials file
timeout           60s                   default`}</code>
              </pre>
              <p className="text-foreground/80 mb-6">
                Every layer is listed with an absolute path and whether it
                exists, because "the file you edited is not the one being read"
                is one of the answers. Credentials are never printed. An address
                a client could never dial — a port outside 1-65535, or a value
                with no port — is reported after the table, naming the layer
                that supplied it, rather than being silently ignored.
              </p>

              <h3 className={h3} style={{ fontFamily: theme.fonts.display }}>
                Top-level keys
              </h3>
              <div className="mb-4">
                <RefTable
                  headers={['Key', 'Default', 'Meaning']}
                  rows={[
                    {
                      k: 'timeout',
                      cells: [code('timeout'), code('60s'), 'Request timeout.'],
                    },
                    {
                      k: 'diff_only',
                      cells: [
                        code('diff_only'),
                        code('false'),
                        'Review only changed files. With neither key set, reviews are diff-only.',
                      ],
                    },
                    {
                      k: 'full_scan',
                      cells: [
                        code('full_scan'),
                        code('false'),
                        'Review the whole repository instead (paid plan).',
                      ],
                    },
                    {
                      k: 'profile',
                      cells: [
                        code('profile'),
                        'none',
                        'Run only the rules in a named profile. A --profile flag overrides it.',
                      ],
                    },
                    {
                      k: 'debug',
                      cells: [
                        code('debug'),
                        code('false'),
                        'Debug logging. MIND_DEBUG and --debug also turn it on.',
                      ],
                    },
                    {
                      k: 'min_severity',
                      cells: [
                        code('min_severity'),
                        'none',
                        'Hide findings below this severity (info, minor, major, blocker).',
                      ],
                    },
                    {
                      k: 'max_severity',
                      cells: [
                        code('max_severity'),
                        'none',
                        'Hide findings above this severity.',
                      ],
                    },
                    {
                      k: 'disabled_analyzers',
                      cells: [
                        code('disabled_analyzers'),
                        'none',
                        'A list of rules to turn off, by full ID, short name, or concern.',
                      ],
                    },
                    {
                      k: 'require_reason',
                      cells: [
                        code('require_reason'),
                        code('false'),
                        'Require a reason on every mind:disable directive.',
                      ],
                    },
                    {
                      k: 'show_suppressed',
                      cells: [
                        code('show_suppressed'),
                        code('false'),
                        'Include suppressed findings in output.',
                      ],
                    },
                    {
                      k: 'gates.go_test',
                      cells: [
                        code('gates.go_test'),
                        code('false'),
                        'Run the go-test gate.',
                      ],
                    },
                    {
                      k: 'gates.cargo_test',
                      cells: [
                        code('gates.cargo_test'),
                        code('false'),
                        'Run the cargo-test gate.',
                      ],
                    },
                    {
                      k: 'gates.pytest',
                      cells: [
                        code('gates.pytest'),
                        code('false'),
                        'Run the pytest gate.',
                      ],
                    },
                    {
                      k: 'gates.js_test',
                      cells: [
                        code('gates.js_test'),
                        code('false'),
                        'Run the JS test gate (jest or vitest, auto-detected).',
                      ],
                    },
                    {
                      k: 'gates.security',
                      cells: [
                        code('gates.security'),
                        code('false'),
                        'Run the security gates (gitleaks secrets, osv-scanner dependency CVEs).',
                      ],
                    },
                    {
                      k: 'output',
                      cells: [
                        code('output'),
                        'none',
                        'Output template overrides. See Output templates below.',
                      ],
                    },
                    {
                      k: 'thresholds',
                      cells: [
                        code('thresholds'),
                        'none',
                        'Per-rule thresholds. See Thresholds below.',
                      ],
                    },
                  ]}
                />
              </div>
              <p className="text-foreground/70 mb-4 text-sm">
                Linters and type checks for all four languages run
                automatically. Each language also has an opt-in test gate:
                go-test, cargo-test, pytest, and a JS test gate that runs jest
                or vitest. See Gates for the full list.
              </p>
              <p className="text-foreground/80 mb-2">
                {code('disabled_analyzers')} accepts a full rule ID, a short
                name, or a concern that matches a whole group. The{' '}
                {code('gate')} concern turns off every gate at once:
              </p>
              <pre className={`${pre} mb-8`} style={codeStyle}>
                <code>{`# .mind/mind.yaml
disabled_analyzers:
  - gate                          # all gates
  - software:code:gate:go-test    # one rule by full id
  - golangci-lint                 # short name also matches`}</code>
              </pre>

              <h3 className={h3} style={{ fontFamily: theme.fonts.display }}>
                Environment variables
              </h3>
              <p className="text-foreground/70 mb-3 text-sm">
                These override the matching config keys.
              </p>
              <div className="mb-8">
                <RefTable
                  headers={['Variable', 'Default', 'Overrides']}
                  rows={[
                    {
                      k: 'MIND_TIMEOUT',
                      cells: [
                        code('MIND_TIMEOUT'),
                        code('60s'),
                        code('timeout'),
                      ],
                    },
                    {
                      k: 'MIND_DEBUG',
                      cells: [code('MIND_DEBUG'), code('false'), code('debug')],
                    },
                    {
                      k: 'MIND_FINDING_TEMPLATE',
                      cells: [
                        code('MIND_FINDING_TEMPLATE'),
                        code('default'),
                        code('output.finding_template'),
                      ],
                    },
                    {
                      k: 'MIND_HEADER_TEMPLATE',
                      cells: [
                        code('MIND_HEADER_TEMPLATE'),
                        code('default'),
                        code('output.header_template'),
                      ],
                    },
                  ]}
                />
              </div>

              <h3 className={h3} style={{ fontFamily: theme.fonts.display }}>
                Thresholds
              </h3>
              <p className="text-foreground/80 mb-3 text-sm">
                Thresholds set when the size, coupling, and duplication checks
                fire. They all live under {code('thresholds')} in{' '}
                {code('mind.yaml')}. A few common ones:
              </p>
              <div className="mb-3">
                <RefTable
                  headers={['Key', 'Default', 'Meaning']}
                  rows={[
                    {
                      k: 'long_function_threshold',
                      cells: [
                        code('long_function_threshold'),
                        code('75'),
                        'Lines before a function is flagged.',
                      ],
                    },
                    {
                      k: 'large_file_threshold',
                      cells: [
                        code('large_file_threshold'),
                        code('1500'),
                        'Lines before a file is flagged.',
                      ],
                    },
                    {
                      k: 'interface_size_threshold',
                      cells: [
                        code('interface_size_threshold'),
                        code('10'),
                        'Methods before an interface is flagged.',
                      ],
                    },
                    {
                      k: 'method_count_major',
                      cells: [
                        code('method_count_major'),
                        code('20'),
                        'Methods before a type must be split.',
                      ],
                    },
                    {
                      k: 'type_cohesion_major',
                      cells: [
                        code('type_cohesion_major'),
                        code('600'),
                        'Cohesion score before a type is flagged.',
                      ],
                    },
                    {
                      k: 'duplicate_code_block_min_lcs_similarity',
                      cells: [
                        code('duplicate_code_block_min_lcs_similarity'),
                        code('75'),
                        'Minimum percent similarity to flag duplicate blocks.',
                      ],
                    },
                  ]}
                />
              </div>
              <p className="text-foreground/70 mb-8 text-sm">
                More keys exist under {code('thresholds')} to adjust coupling,
                duplication, and complexity limits.
              </p>

              <h3 className={h3} style={{ fontFamily: theme.fonts.display }}>
                Output templates
              </h3>
              <p className="text-foreground/80 mb-3 text-sm">
                Customize what your agent sees in the stop hook output.{' '}
                {code('finding_template')} and {code('header_template')} (under{' '}
                {code('output')}) each accept {code('default')} or a path to a
                Go {code('text/template')} {code('.tmpl')} file.{' '}
                {code('analyzer_templates')} maps a rule short name to its own
                template.
              </p>
              <p className="text-foreground/80 mb-2 text-sm">
                Template keys, per finding: {code('Severity')},{' '}
                {code('ShortName')}, {code('RuleID')}, {code('FilePath')},{' '}
                {code('StartLine')}, {code('EndLine')}, {code('Description')},{' '}
                {code('Remediation')}, {code('Value')}, {code('Threshold')}. For
                the header: {code('TotalCount')}, {code('BlockerCount')},{' '}
                {code('MajorCount')}, {code('MinorCount')}, {code('InfoCount')},{' '}
                {code('BatchSize')}, {code('HasMore')}, {code('Findings')}.
              </p>
              <p className="text-foreground/80 mb-2 text-sm">
                The default finding template:
              </p>
              <pre className={pre} style={codeStyle}>
                <code>{`[{{.Severity}}] {{.ShortName}} — {{.FilePath}}:{{.StartLine}}: {{.Description}}{{if .Remediation}}
  fix: {{.Remediation}}{{end}}`}</code>
              </pre>
            </section>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Docs() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[800px]">
              <Skeleton className="mx-auto mt-2 mb-8 h-12 w-[40%]" />
              <Skeleton className="mx-auto mb-4 h-6 w-[80%]" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <DocsContent />
    </Suspense>
  );
}
